import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Package, Sparkles, TrendingUp, X } from "lucide-react";
import { billingApi } from "@/api/billing.api";
import { getAppBridge } from "@/api/client";
import { redirectToUrl } from "@/utils/redirect";
import { useToast } from "@/hooks/useToast";
import { toErrorMessage } from "@/utils/errorMessage";
import { UPGRADE_PRORATION_NOTE } from "@/utils/planLadder";
import { useLifecycleNudge } from "@/hooks/useLifecycleNudge";
import type { NudgeEvidence, NudgeOption, NudgeTriggerKey } from "@/types/nudge.types";

/**
 * Copy lives here, not in the backend.
 *
 * `lifecycle-nudge.types.ts` says so explicitly: the engine ships identifiers
 * and numbers only so a price or wording change has exactly one home. Every
 * trigger the backend can return needs an entry, or the merchant gets a banner
 * with no headline.
 */
const NUDGE_COPY: Record<NudgeTriggerKey, { headline: string; body: string }> = {
  free_to_launch: {
    headline: "Ready to put AR on the rest of your catalog?",
    body: "You've seen it work on your storefront. A plan keeps the models coming and unlocks higher-quality output for the products that earn the most.",
  },
  launch_to_growth: {
    headline: "You're running out of room this month",
    body: "Moving up a plan gives you the headroom permanently — and costs less per product than topping up.",
  },
  growth_to_scale: {
    headline: "You're running out of room this month",
    body: "Moving up a plan gives you the headroom permanently — and costs less per product than topping up.",
  },
  premium_exhausted: {
    headline: "You've used every Premium Generation included this month",
    body: "Premium Generation is what you reach for when a product needs better shape accuracy and detail than the standard pass gives.",
  },
  trial_expired: {
    headline: "Your free AR window has closed",
    body: "Your generated models stay live. Choose a plan when you want to add more products to AR.",
  },
};

/** Accent per trigger, matching the surrounding billing surfaces. */
const NUDGE_ICON: Record<NudgeTriggerKey, typeof TrendingUp> = {
  free_to_launch: Sparkles,
  launch_to_growth: TrendingUp,
  growth_to_scale: TrendingUp,
  premium_exhausted: Sparkles,
  trial_expired: Package,
};

/** The merchant's own numbers, rendered only where the backend supplied them. */
function evidenceLines(evidence: NudgeEvidence): string[] {
  const lines: string[] = [];

  if (evidence.products_quota > 0) {
    const percent =
      evidence.quota_percent_used !== null ? ` (${evidence.quota_percent_used}%)` : "";
    lines.push(
      `${evidence.products_used} of ${evidence.products_quota} products used this period${percent}`,
    );
  }

  if (evidence.premium_included !== null && evidence.premium_included > 0) {
    lines.push(
      `${evidence.premium_used ?? 0} of ${evidence.premium_included} Premium Generations used`,
    );
  }

  if (evidence.ar_views !== null) {
    lines.push(`${evidence.ar_views} AR views this period`);
  }

  if (evidence.ar_launches !== null) {
    lines.push(`${evidence.ar_launches} AR sessions launched`);
  }

  return lines;
}

function optionLabel(option: NudgeOption): string {
  if (option.kind === "upgrade") {
    const name = option.target
      ? option.target.charAt(0).toUpperCase() + option.target.slice(1)
      : "the next plan";
    return `Upgrade to ${name}`;
  }
  if (option.kind === "pack") {
    if (option.target === "premium_generation") {
      return option.credits && option.credits > 1
        ? `Add ${option.credits} Premium Generations`
        : "Add a Premium Generation";
    }
    return option.credits ? `Add ${option.credits} more products` : "Add extra products";
  }
  return "See plans";
}

function optionDetail(option: NudgeOption): string | null {
  if (option.price === null) return null;
  if (option.kind === "upgrade") {
    const perProduct =
      option.credits && option.credits > 0
        ? ` — $${(option.price / option.credits).toFixed(2)} per product`
        : "";
    return `$${option.price}/mo${perProduct}`;
  }
  if (option.kind === "pack") {
    return `$${option.price} one-time`;
  }
  return null;
}

/**
 * A single lifecycle nudge, or nothing at all.
 *
 * Fails closed by design: no nudge, still loading, or a failed lookup all
 * render `null`. A billing banner that flashes an error at a merchant is worse
 * than silence, and `billingApi.getNudge` already collapses failures to null.
 */
export function LifecycleNudgeBanner() {
  const { nudge, loading, dismiss } = useLifecycleNudge();
  const { showError } = useToast();
  const navigate = useNavigate();
  // Index of the option currently redirecting, so only that button spins and a
  // second click cannot open a second checkout.
  const [busyOption, setBusyOption] = useState<number | null>(null);
  const [dismissing, setDismissing] = useState(false);

  if (loading || !nudge) return null;

  const copy = NUDGE_COPY[nudge.trigger_key];
  if (!copy) return null;
  const Icon = NUDGE_ICON[nudge.trigger_key];
  const lines = evidenceLines(nudge.evidence);

  const handleOption = async (option: NudgeOption, index: number) => {
    if (busyOption !== null) return;

    // Nothing to buy — the plan choice is a real decision and belongs on the
    // pricing page (matches `PostTrialBanner`'s "Choose a plan").
    if (option.kind === "acknowledge" || !option.target) {
      navigate("/billing");
      return;
    }

    setBusyOption(index);
    try {
      // Same checkout paths the rest of billing uses — `QuotaCapModal` for the
      // upgrade, `ExtraPackOffer` for the packs. Deliberately not a parallel
      // flow: these endpoints enforce availability server-side.
      const { checkout_url } =
        option.kind === "upgrade"
          ? await billingApi.createCheckoutSession(option.target)
          : option.target === "premium_generation"
            ? await billingApi.createPremiumGenerationUpgradeCheckout()
            : await billingApi.createExtraProductPackCheckout();

      if (!checkout_url || typeof checkout_url !== "string") {
        throw new Error("Checkout URL missing from server response");
      }
      // MUST go through App Bridge — a bare redirect is blocked by the Admin
      // iframe CSP and the merchant would click and see nothing happen.
      redirectToUrl(checkout_url, getAppBridge());
    } catch (error: unknown) {
      showError(toErrorMessage(error, "Failed to create checkout. Please try again."));
      setBusyOption(null);
    }
  };

  const handleDismiss = () => {
    if (dismissing) return;
    setDismissing(true);
    // Not awaited: the hook hides the banner optimistically, so there is
    // nothing left on screen for the promise to update.
    void dismiss(nudge.trigger_key);
  };

  return (
    <div className="glass-panel relative border-indigo-100 bg-gradient-to-r from-indigo-50/60 to-sky-50/60">
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-slate-400 transition hover:bg-white hover:text-slate-600"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3 pr-10">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 shadow-lg shadow-indigo-200">
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900">{copy.headline}</h3>
            <p className="mt-1 text-sm text-slate-600">{copy.body}</p>
          </div>
        </div>

        {lines.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {lines.map((line) => (
              <span
                key={line}
                className="rounded-2xl border border-indigo-200 bg-white/80 px-3 py-1.5 text-xs font-medium text-slate-600"
              >
                {line}
              </span>
            ))}
          </div>
        )}

        {/* Rendered in backend array order, on purpose. The engine guarantees
            the upgrade is index 0 whenever one exists, with the one-time pack
            behind it as the decoy (the "two-option close"). Sorting, filtering
            or reordering here silently breaks that pricing decision. */}
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          {nudge.options.map((option, index) => {
            const primary = index === 0;
            const detail = optionDetail(option);
            return (
              <button
                key={`${option.kind}-${option.target ?? "none"}`}
                type="button"
                onClick={() => handleOption(option, index)}
                disabled={busyOption !== null}
                className={
                  primary
                    ? "flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-6 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
                    : "flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white/80 px-6 py-2 text-sm font-semibold text-slate-700 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                }
              >
                {busyOption === index ? (
                  "Redirecting..."
                ) : (
                  <>
                    {optionLabel(option)}
                    {detail && (
                      <span className={primary ? "font-normal text-white/80" : "font-normal text-slate-500"}>
                        {detail}
                      </span>
                    )}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            );
          })}
        </div>

        {nudge.options.some((option) => option.prorated) && (
          <p className="text-xs text-slate-500">{UPGRADE_PRORATION_NOTE}</p>
        )}
      </div>
    </div>
  );
}
